import React, { useEffect, useState } from 'react';
import { Card, Row, Col, Button, Spinner } from 'react-bootstrap';
import { useGetCommissionsMutation } from '../../api/usersAPISlices';
import { formatCurrency, formatNumber } from '../../utils/formatters';

const CommissionCard = () => {
    const [commissionData, setCommissionData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [currentPeriod, setCurrentPeriod] = useState('current');
    
    const [getCommissions] = useGetCommissionsMutation();
    const token = JSON.parse(localStorage.getItem("token"));

    const fetchCommissionData = async (period) => {
        setIsLoading(true);
        try {
            const response = await getCommissions({ 
                token, 
                period 
            }).unwrap();
            setCommissionData(response);
        } catch (error) {
            console.error('Error fetching commission data:', error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchCommissionData(currentPeriod);
    }, [currentPeriod]);

    const handlePeriodChange = (period) => {
        setCurrentPeriod(period);
    };

    if (!commissionData && !isLoading) {
        return null;
    }

    return (
        <Card className="commission-card mb-4" style={{ borderRadius: '12px', border: '1px solid #e5e5e5', height: '100%', minHeight: '420px' }}>
            <Card.Body className="p-4">
                <div className="d-flex justify-content-between align-items-start mb-3">
                    <div>
                        <h5 className="font-weight-bold mb-1">Developer Commission</h5>
                        {commissionData && (
                            <p className="text-muted small mb-0">
                                {commissionData.period_label}
                            </p>
                        )}
                    </div>
                    <div>
                        <Button 
                            size="sm" 
                            variant={currentPeriod === 'current' ? 'primary' : 'outline-primary'}
                            className="me-2"
                            onClick={() => handlePeriodChange('current')}
                            style={{ fontSize: '12px', padding: '4px 8px' }}
                        >
                            Current
                        </Button>
                        <Button 
                            size="sm" 
                            variant={currentPeriod === 'previous' ? 'primary' : 'outline-primary'}
                            onClick={() => handlePeriodChange('previous')}
                            style={{ fontSize: '12px', padding: '4px 8px' }}
                        >
                            Previous
                        </Button>
                    </div>
                </div>
                
                {isLoading ? (
                    <div className="text-center py-4">
                        <Spinner animation="border" variant="primary" size="sm" />
                        <p className="mt-2 text-muted small">Loading commission data...</p>
                    </div>
                ) : commissionData ? (
                    <div>
                        {/* Total Commission */}
                        <div className="mb-4">
                            <div className="d-flex justify-content-between align-items-center">
                                <span className="text-muted small">Total Commission Owed</span>
                                <h3 className="mb-0 font-weight-bold text-primary">
                                    {formatCurrency(commissionData.total_commission)}
                                </h3>
                            </div>
                        </div>
                        
                        {/* Commission Breakdown */}
                        <div className="mb-3">
                            <h6 className="font-weight-bold mb-3">Commission Breakdown</h6>
                            <div className="mb-2">
                                <div className="d-flex justify-content-between align-items-center">
                                    <span className="text-muted small">Default Source (15%)</span>
                                    <span className="font-weight-bold">
                                        {formatCurrency(commissionData.default_source_commission)}
                                    </span>
                                </div>
                            </div>
                            <div className="mb-2">
                                <div className="d-flex justify-content-between align-items-center">
                                    <span className="text-muted small">Other Sources (25%)</span>
                                    <span className="font-weight-bold">
                                        {formatCurrency(commissionData.other_source_commission)}
                                    </span>
                                </div>
                            </div>
                        </div>
                        
                        <hr className="my-3" />
                        
                        {/* Lead Statistics */}
                        <div>
                            <h6 className="font-weight-bold mb-3">Lead Statistics</h6>
                            <div className="mb-2">
                                <div className="d-flex justify-content-between align-items-center">
                                    <span className="text-muted small">Total Leads Processed</span>
                                    <span className="font-weight-bold">{formatNumber(commissionData.total_lead_count)}</span>
                                </div>
                            </div>
                            <div className="mb-2">
                                <div className="d-flex justify-content-between align-items-center">
                                    <span className="text-muted small">Default Source Leads</span>
                                    <span>{formatNumber(commissionData.default_source_lead_count)}</span>
                                </div>
                            </div>
                            <div>
                                <div className="d-flex justify-content-between align-items-center">
                                    <span className="text-muted small">Other Source Leads</span>
                                    <span>{formatNumber(commissionData.other_source_lead_count)}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="text-center py-4">
                        <p className="text-muted">No commission data available</p>
                    </div>
                )}
            </Card.Body>
        </Card>
    );
};

export default CommissionCard;